import { DatePicker } from '@liza/ui/components/atoms/DatePicker';
import { Alert, AlertDescription } from '@liza/ui/components/ui/alert';
import { Button } from '@liza/ui/components/ui/button';
import { Checkbox } from '@liza/ui/components/ui/checkbox';
import { Field, FieldContent, FieldGroup, FieldLabel } from '@liza/ui/components/ui/field';
import { Input } from '@liza/ui/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@liza/ui/components/ui/select';
import { DaysPerWeekCountField } from '@liza/ui/components/timing/DaysPerWeekCountField';
import { EndsRow } from '@liza/ui/components/timing/EndsRow';
import { ScheduleTypeTabs } from '@liza/ui/components/timing/ScheduleTypeTabs';
import { StartsRow } from '@liza/ui/components/timing/StartsRow';
import { TimeOfDayRow } from '@liza/ui/components/timing/TimeOfDayRow';
import { useScheduleTypeLabels } from '@liza/ui/hooks/useScheduleTypeLabels';
import { formatTimingAsHumanReadable } from '@liza/ui/utils/formatTiming';
import {
  SHARED_SCHEDULE_TYPES,
  buildTimingRepeat,
  DAY_LABELS,
  deriveStateFromTiming,
  FULL_DAY_NAMES,
  HOURLY_PRESET_OPTIONS,
  isLegacyCustomTiming,
  nearestHourlyPreset,
  makeDaySelection,
  makeDefaultState,
  type CustomUnit,
  type InitialTimingValue,
  type SchedulePickerState,
  type ScheduleType,
  type TimingRepeatValue,
} from '@liza/ui/utils/schedulePicker';
import { format, parseISO } from 'date-fns';
import { ChevronDown, Info } from 'lucide-react';
import { cn } from '../utils/cn';
import { DATE_FORMATS } from '../utils/date';
import { useEffect, useId, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

export { DueDateFieldSchema, SHARED_SCHEDULE_TYPES } from '@liza/ui/utils/schedulePicker';
export type { InitialTimingValue, ScheduleType, TimingRepeatValue } from '@liza/ui/utils/schedulePicker';

// ============================================================================
// Component
// ============================================================================

export interface TaskTimingPickerProps {
  defaultScheduleType?: ScheduleType;
  /** Existing timing to initialize from (for edit mode). */
  initialTiming?: InitialTimingValue;
  /** Initial due date value (YYYY-MM-DD) for edit mode. */
  initialDueDate?: string;
  /**
   * Edit mode: the item's stored frequency, when it cannot be inferred from the other initial
   * values. Needed only for a "Once" item with no due date — it has no timing and no date, so it is
   * indistinguishable from a blank item. Ignored when `initialTiming` or `initialDueDate` is set,
   * since those name the frequency themselves.
   */
  initialScheduleType?: ScheduleType;
  /**
   * Suppress `onChange` while the host cannot accept a schedule yet — e.g. the prescribe dialog
   * disables its frequency section until a medication is chosen.
   *
   * The mount-time `onChange` below reports the default mode whether or not the user has engaged, so
   * a host that renders this behind a disabled fieldset would otherwise bank that default (Daily) as
   * a deliberate choice. Keeping it un-reported means a medication cannot silently acquire a daily
   * schedule the prescriber never picked (#8100).
   */
  suppressChange?: boolean;
  /** Initial state of the "As needed" (PRN) checkbox. For edit mode. */
  initialAsNeeded?: boolean;
  /** Restrict which frequencies are visible in the toggle group. */
  supportedScheduleTypes?: readonly ScheduleType[];
  /** Whether the host can persist a due date. False hides the field on "Once", so a host whose
   * endpoint has no due-date concept — the records medication dialog writes a `MedicationStatement`,
   * which has none — cannot collect a date it would then drop on submit. */
  supportsDueDate?: boolean;
  /** Start with the schedule controls open instead of just the collapsed sentence. */
  defaultExpanded?: boolean;
  /** Called whenever the frequency, due date or PRN flag changes. timing=null means "Once" (no
   * repeating); dueDate is only set for "Once". `asNeeded` is an independent axis — it can be true
   * alongside any frequency ("every 4 hours as needed"). */
  onChange: (timing: TimingRepeatValue | null, dueDate: string, asNeeded: boolean) => void;
  /** Called whenever the picker's validity changes (e.g. weekly with zero days selected is invalid). */
  onValidChange?: (valid: boolean) => void;
}

/**
 * Surface the quota mode when edit-mode state derived into it even if the
 * host form hasn't opted in — otherwise a legacy N-per-week timing renders
 * with no selected tab.
 */
function visibleScheduleTypesFor(current: ScheduleType, supported: readonly ScheduleType[]): readonly ScheduleType[] {
  return current === 'daysPerWeek' && !supported.includes('daysPerWeek') ? [...supported, 'daysPerWeek'] : supported;
}

/**
 * A due date dates the single occurrence a "Once" item is, so it is offered on Once and nowhere else
 * — a repeating schedule carries its own end date instead. The as-needed box does not withdraw it:
 * "once, by 1 Jan, as needed" is a thing to do if it is needed, by a date (#10259). A host whose
 * endpoint has no due-date concept withdraws it via `supportsDueDate`.
 */
function showDueDateFor(scheduleType: ScheduleType, supportsDueDate: boolean): boolean {
  return supportsDueDate && scheduleType === 'once';
}

export function TaskTimingPicker({
  defaultScheduleType = 'once',
  initialTiming,
  initialDueDate = '',
  initialAsNeeded = false,
  initialScheduleType,
  suppressChange = false,
  supportedScheduleTypes = SHARED_SCHEDULE_TYPES,
  supportsDueDate = true,
  defaultExpanded = false,
  onChange,
  onValidChange,
}: TaskTimingPickerProps): React.ReactNode {
  const effectiveDefault = supportedScheduleTypes.includes(defaultScheduleType)
    ? defaultScheduleType
    : (supportedScheduleTypes.at(0) ?? 'once');
  const { t } = useTranslation('common');
  const asNeededId = useId();
  // Frequency derivation for edit mode, in signal order: a stored timing names its own shape;
  // otherwise a stored due date identifies Once. Only a genuinely blank item falls through to the
  // host's default. Without the `initialDueDate` arm a saved "Once" reopened on the default mode and
  // the mount-time `onChange` below rewrote it — a dateless Once still has no signal of its own, so
  // hosts that support it pass `initialScheduleType` (#8100).
  const [state, setState] = useState<SchedulePickerState>(() => {
    if (initialTiming) return deriveStateFromTiming(initialTiming);
    if (initialDueDate) return makeDefaultState('once');
    return makeDefaultState(initialScheduleType ?? effectiveDefault);
  });
  const [dueDate, setDueDate] = useState(initialDueDate);
  const [asNeeded, setAsNeeded] = useState(initialAsNeeded);
  const [expanded, setExpanded] = useState(defaultExpanded);

  const showDueDate = showDueDateFor(state.scheduleType, supportsDueDate);

  const onChangeRef = useRef(onChange);
  const onValidChangeRef = useRef(onValidChange);
  useEffect(() => {
    onChangeRef.current = onChange;
    onValidChangeRef.current = onValidChange;
  });

  useEffect(() => {
    if (suppressChange) return;
    const timing = buildTimingRepeat(state);
    const isValid = state.scheduleType === 'once' || timing !== null;
    onValidChangeRef.current?.(isValid);
    if (!isValid) return;
    onChangeRef.current(timing, showDueDate ? dueDate : '', asNeeded);
  }, [state, dueDate, asNeeded, showDueDate, suppressChange]);

  const updateState = (patch: Partial<SchedulePickerState>): void => {
    setState((prev) => ({ ...prev, ...patch }));
  };

  const handleScheduleTypeChange = (value: ScheduleType): void => {
    const patch: Partial<SchedulePickerState> = { scheduleType: value };
    if (value === 'simple') {
      if (!state.selectedDays.some(Boolean) || state.scheduleType === 'once') {
        patch.selectedDays = makeDaySelection();
      }
    }
    updateState(patch);
  };

  const handleCustomUnitChange = (customUnit: CustomUnit): void => {
    // Entering hours snaps the free-form interval onto a preset; leaving it
    // keeps the number as-is.
    updateState(
      customUnit === 'hours'
        ? { customUnit, customInterval: nearestHourlyPreset(state.customInterval) }
        : { customUnit },
    );
  };

  const toggleDay = (index: number): void => {
    setState((prev) => {
      const next = [...prev.selectedDays];
      next[index] = !next[index];
      return { ...prev, selectedDays: next };
    });
  };

  const visibleScheduleTypes = visibleScheduleTypesFor(state.scheduleType, supportedScheduleTypes);
  const modeLabels = useScheduleTypeLabels();

  const isHourlyEvery = state.scheduleType === 'custom' && state.customUnit === 'hours';
  const showSimpleDayPicker = state.scheduleType === 'simple';
  const showCustomFrequency = state.scheduleType === 'custom';

  // The live human-readable schedule shown on the collapsed header, updating as the controls
  // change. The PRN flag is not folded in — its checkbox sits right below, visible either way.
  const sentence = useMemo(() => {
    if (state.scheduleType === 'once') {
      return showDueDate && dueDate
        ? t('dueDateLabel', { date: format(parseISO(dueDate), DATE_FORMATS.DATE) })
        : t('once');
    }
    const timing = buildTimingRepeat(state);
    // Null timing = the mode's controls are in an invalid state (e.g. no days
    // selected); fall back to the current mode's label rather than a sentence.
    return timing ? formatTimingAsHumanReadable(timing) : modeLabels[state.scheduleType];
  }, [state, dueDate, showDueDate, t, modeLabels]);

  // Surface a callout when the picker was opened on a legacy timing the editor
  // can't fully represent (e.g. an ambiguous bare weekly frequency, or a daily
  // frequency above the multi-daily presets). Shown regardless of the active
  // schedule toggle — re-saving from any mode overwrites the legacy fields.
  // Tied to the *initial* prop so it doesn't flicker as the user edits.
  const showLegacyCallout = useMemo(() => !!initialTiming && isLegacyCustomTiming(initialTiming), [initialTiming]);

  return (
    <div className="space-y-3">
      <FieldGroup>
        <Field>
          <FieldLabel>{t('scheduleLabel')}</FieldLabel>
          <Button
            type="button"
            variant="outline"
            className="w-full justify-between font-normal"
            onClick={() => setExpanded((prev) => !prev)}
            aria-expanded={expanded}
            data-testid="schedule-editor-toggle"
          >
            <span className="truncate">{sentence}</span>
            <ChevronDown className={cn('size-4 shrink-0 transition-transform', expanded && 'rotate-180')} />
          </Button>
        </Field>
      </FieldGroup>

      {showLegacyCallout && (
        <Alert>
          <Info />
          <AlertDescription>{t('customLegacyShapeWarning')}</AlertDescription>
        </Alert>
      )}

      {expanded && (
        <div className="space-y-3 rounded-md border p-3" data-testid="schedule-editor-controls">
          <ScheduleTypeTabs
            value={state.scheduleType}
            visibleTypes={visibleScheduleTypes}
            onValueChange={handleScheduleTypeChange}
          />

          {showSimpleDayPicker && (
            <FieldGroup>
              <Field>
                <FieldLabel>{t('daysLabel')}</FieldLabel>
                <FieldContent className="flex flex-row gap-1">
                  {DAY_LABELS.map((label, i) => (
                    <Button
                      key={label}
                      type="button"
                      variant={state.selectedDays[i] ? 'default' : 'secondary'}
                      size="xs"
                      onClick={() => toggleDay(i)}
                      className="flex-1"
                      aria-label={`${FULL_DAY_NAMES[i]} ${state.selectedDays[i] ? '(selected)' : ''}`}
                      aria-pressed={state.selectedDays[i]}
                    >
                      {label}
                    </Button>
                  ))}
                </FieldContent>
              </Field>
            </FieldGroup>
          )}

          {state.scheduleType === 'daysPerWeek' && (
            <DaysPerWeekCountField
              count={state.daysPerWeekCount}
              onCountChange={(daysPerWeekCount) => updateState({ daysPerWeekCount })}
            />
          )}

          {showCustomFrequency && (
            <FieldGroup>
              <Field>
                <FieldLabel>{t('customFrequencyLabel')}</FieldLabel>
                <FieldContent className="flex flex-row items-center gap-1">
                  <span className="text-sm text-muted-foreground">{t('every')}</span>
                  {isHourlyEvery ? (
                    // Hourly is preset-only (design decision) — a fixed set
                    // instead of the free interval input.
                    <Select
                      value={String(state.customInterval)}
                      onValueChange={(v) => updateState({ customInterval: parseInt(v) })}
                    >
                      <SelectTrigger aria-label={t('repeatInterval')} className="w-auto flex-1">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {HOURLY_PRESET_OPTIONS.map((preset) => (
                          <SelectItem key={preset} value={String(preset)}>
                            {preset}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  ) : (
                    <Input
                      type="number"
                      min={1}
                      max={365}
                      value={state.customInterval}
                      onChange={(e) => updateState({ customInterval: Math.max(1, parseInt(e.target.value) || 1) })}
                      className="w-auto flex-1"
                      aria-label={t('repeatInterval')}
                    />
                  )}
                  <Select value={state.customUnit} onValueChange={(v) => handleCustomUnitChange(v as CustomUnit)}>
                    <SelectTrigger aria-label={t('periodUnit')} className="w-auto flex-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="hours">{t('hours')}</SelectItem>
                      <SelectItem value="days">{t('days')}</SelectItem>
                      <SelectItem value="weeks">{t('weeks')}</SelectItem>
                      <SelectItem value="months">{t('months')}</SelectItem>
                      <SelectItem value="years">{t('years')}</SelectItem>
                    </SelectContent>
                  </Select>
                </FieldContent>
              </Field>
            </FieldGroup>
          )}

          <TimeOfDayRow
            scheduleType={state.scheduleType}
            hourly={isHourlyEvery}
            whenCodes={state.whenCodes}
            onChange={(whenCodes) => updateState({ whenCodes })}
          />

          <StartsRow
            scheduleType={state.scheduleType}
            startDate={state.startDate}
            onChange={(startDate) => updateState({ startDate })}
          />

          <EndsRow
            scheduleType={state.scheduleType}
            endMode={state.endMode}
            endDate={state.endDate}
            endCount={state.endCount}
            onPatch={updateState}
          />

          {showDueDate && (
            <FieldGroup data-testid="due-date-field">
              <Field>
                <FieldLabel>{t('dueDateOptional')}</FieldLabel>
                <FieldContent>
                  <DatePicker
                    value={dueDate}
                    onChange={(value) => setDueDate(value)}
                    aria-label={t('dueDateOptional')}
                  />
                </FieldContent>
              </Field>
            </FieldGroup>
          )}
        </div>
      )}

      {/* PRN is its own axis, so it sits outside the collapsible frequency editor and stays visible
          either way — "every 4 hours as needed" is one item, not a choice between two (#10259). */}
      <Field orientation="horizontal">
        <Checkbox
          id={asNeededId}
          checked={asNeeded}
          onCheckedChange={(checked) => setAsNeeded(checked === true)}
          data-testid="as-needed-checkbox"
        />
        <FieldLabel htmlFor={asNeededId} className="font-normal">
          {t('asNeeded')}
        </FieldLabel>
      </Field>
    </div>
  );
}
